import { Appearance, Pressable, StyleSheet, Text, View } from 'react-native';

/**
 * Shared crash-fallback screen for the app-root and per-route error
 * boundaries. Intentionally uses bare React Native primitives — no Tamagui,
 * no theme provider — so it renders even when those providers are what
 * threw. Dark mode is read from the OS via `Appearance` (a static API needing
 * no context), so error screens don't flash white-on-white for dark users.
 *
 * Both boundaries render this so they stay one visual system; only the copy
 * and the recovery action differ.
 */

const PALETTES = {
  light: {
    background: '#F5F5F0',
    title: '#1A1A1A',
    body: '#6B7280',
    buttonBg: '#1A1A1A',
    buttonText: '#FFFFFF',
  },
  dark: {
    background: '#1A1A1A',
    title: '#F5F5F0',
    body: '#9CA3AF',
    buttonBg: '#F5F5F0',
    buttonText: '#1A1A1A',
  },
} as const;

export interface ErrorFallbackScreenProps {
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
}

export function ErrorFallbackScreen({
  title,
  message,
  actionLabel,
  onAction,
}: ErrorFallbackScreenProps) {
  const scheme = Appearance.getColorScheme() === 'dark' ? 'dark' : 'light';
  const c = PALETTES[scheme];

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <Text style={[styles.title, { color: c.title }]}>{title}</Text>
      <Text style={[styles.message, { color: c.body }]}>{message}</Text>
      <Pressable
        style={[styles.button, { backgroundColor: c.buttonBg }]}
        onPress={onAction}
        accessibilityRole="button"
        accessibilityLabel={actionLabel}
      >
        <Text style={[styles.buttonText, { color: c.buttonText }]}>{actionLabel}</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 20,
  },
  button: {
    borderRadius: 10,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
  },
});
